import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, CheckCircle2, Check, Clock, Target, Eye } from "lucide-react";
import { toast } from "sonner";
import { TutorDrawer } from "@/components/TutorDrawer";
import { useLessonHeartbeat } from "@/hooks/useLessonHeartbeat";
import { useLessonNarration, splitIntoSentences } from "@/hooks/useLessonNarration";
import { LessonAudioBar } from "@/components/LessonAudioBar";

export default function Lesson() {
  const { lessonId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);

  // Time-on-task: passive heartbeat every 30 sec while tab is visible
  const { minutesStudied } = useLessonHeartbeat(lessonId);

  // Narration (browser-native TTS) + sentence highlighting
  const narration = useLessonNarration();
  const activeSentenceRef = useRef(null);

  // Build flat list of {id, text} sentences spanning every section (memoised so
  // narration playback won't restart on re-render).
  const sentences = useMemo(() => {
    const lesson = data?.lesson;
    if (!lesson?.sections) return [];
    const list = [];
    lesson.sections.forEach((s, secIdx) => {
      if (s.heading) {
        list.push({ id: `sec-${secIdx}-h`, text: s.heading, sectionIdx: secIdx, isHeading: true });
      }
      splitIntoSentences(s.body).forEach((text, sIdx) => {
        list.push({ id: `sec-${secIdx}-s-${sIdx}`, text, sectionIdx: secIdx });
      });
    });
    return list;
  }, [data]);

  // Auto-scroll the active sentence into view smoothly
  useEffect(() => {
    if (!narration.currentSentenceId) return;
    const el = document.querySelector(`[data-sentence-id="${narration.currentSentenceId}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      const outOfView = rect.top < 80 || rect.bottom > window.innerHeight - 200;
      if (outOfView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      activeSentenceRef.current = el;
    }
  }, [narration.currentSentenceId]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: d }, { data: p }] = await Promise.all([
          api.get(`/curriculum/lesson/${lessonId}`),
          api.get("/progress"),
        ]);
        setData(d);
        setCompleted((p.lessons_completed || []).includes(lessonId));
      } catch {
        toast.error("Couldn't load lesson");
        nav("/dashboard");
      }
    })();
  }, [lessonId, nav]);

  const markComplete = async () => {
    setCompleting(true);
    try {
      await api.post("/progress/lesson-complete", { lesson_id: lessonId });
      setCompleted(true);
      toast.success("Lesson marked complete");
    } catch {
      toast.error("Couldn't save progress");
    } finally {
      setCompleting(false);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-5xl mx-auto px-6 py-20 text-zinc-500">Loading lesson…</div>
      </div>
    );
  }

  const { lesson, module: mod } = data;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid={`lesson-page-${lessonId}`}>
        <Link to={`/module/${mod.id}`} className="text-sm text-zinc-600 hover:text-red-600 inline-flex items-center gap-1 mb-6" data-testid="back-to-module-link">
          <ArrowLeft className="w-4 h-4" /> {mod.title}
        </Link>

        <div className="overline mb-3"><span className="red-bar" />Module {mod.order} · Lesson</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-3" data-testid="lesson-title">{lesson.title}</h1>
        <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest mb-8 flex items-center gap-4 flex-wrap">
          <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {lesson.hours} hours</span>
          {minutesStudied !== null && minutesStudied > 0 && (
            <span className="inline-flex items-center gap-1.5 text-zinc-700" data-testid="lesson-verified-time" title="Verified study time on this lesson — automatically tracked while the tab is active">
              <Eye className="w-3.5 h-3.5" /> {minutesStudied} min studied
            </span>
          )}
          {completed && <span className="inline-flex items-center gap-1.5 text-green-700"><CheckCircle2 className="w-3.5 h-3.5" /> Completed</span>}
        </div>

        {/* Audio narration bar */}
        <div className="mb-8" data-testid="lesson-audio-wrapper">
          <LessonAudioBar narration={narration} sentences={sentences} />
        </div>

        {/* Learning Outcomes */}
        <div className="card-tech p-6 mb-10" data-testid="lesson-outcomes">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-red-600" />
            <div className="overline">Learning outcomes</div>
          </div>
          <ul className="space-y-2 text-sm text-zinc-800">
            {lesson.outcomes.map((o, i) => (
              <li key={`outcome-${i}-${o.slice(0, 30)}`} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" strokeWidth={3} />
                {o}
              </li>
            ))}
          </ul>
        </div>

        {/* Sections — each sentence is a span with data-sentence-id for narration highlighting */}
        <article className="lesson-prose" data-testid="lesson-content">
          {lesson.sections.map((s, secIdx) => {
            const sentencesInSection = splitIntoSentences(s.body);
            const headingId = `sec-${secIdx}-h`;
            return (
              <section key={`sec-${secIdx}-${s.heading?.slice(0, 30) || ''}`} className="fade-in">
                <h3
                  data-sentence-id={headingId}
                  className={narration.currentSentenceId === headingId ? "narration-active" : ""}
                >
                  {s.heading}
                </h3>
                <p>
                  {sentencesInSection.map((text, sIdx) => {
                    const id = `sec-${secIdx}-s-${sIdx}`;
                    const active = narration.currentSentenceId === id;
                    return (
                      <span
                        key={id}
                        data-sentence-id={id}
                        className={active ? "narration-active" : ""}
                      >
                        {text}
                        {sIdx < sentencesInSection.length - 1 ? " " : ""}
                      </span>
                    );
                  })}
                </p>
              </section>
            );
          })}
        </article>

        {/* Footer actions */}
        <div className="mt-12 pt-8 border-t border-zinc-200 flex flex-wrap gap-3 items-center justify-between">
          <button
            onClick={markComplete}
            disabled={completed || completing}
            className={`btn-primary ${completed ? "opacity-60 cursor-default" : ""}`}
            data-testid="mark-complete-btn"
          >
            {completed ? (<><CheckCircle2 className="w-4 h-4" /> Completed</>) : completing ? "Saving…" : (<><Check className="w-4 h-4" /> Mark complete</>)}
          </button>
          {lesson.quiz_id && (
            <Link to={`/quiz/${lesson.quiz_id}`} data-testid="take-quiz-btn">
              <button className="btn-outline">Take the lesson quiz <ArrowRight className="w-4 h-4" /></button>
            </Link>
          )}
        </div>
      </main>

      <TutorDrawer lessonTitle={lesson.title} />
    </div>
  );
}
